import { useEffect, useState, useCallback } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import { MemeSubmission, Sticker, CustomMemeTemplate } from "../types";

interface User {
  id: string;
  name: string;
  first_seen_at: string;
  last_seen_at: string;
  meme_count: number;
}
import { uploadImageWithThumbnail } from "../utils/imageOptimization";
import {
  AdminHeader,
  AdminTabs,
  MemesGrid,
  StickersGrid,
  TemplatesGrid,
  UsersGrid,
  UploadModal,
  UploadTemplateModal,
} from "../components/admin";

const AdminDashboard = () => {
  const { signOut } = useAuth();
  const [memes, setMemes] = useState<MemeSubmission[]>([]);
  const [stickers, setStickers] = useState<Sticker[]>([]);
  const [templates, setTemplates] = useState<CustomMemeTemplate[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "memes" | "stickers" | "templates" | "users"
  >("memes");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [memeFilter, setMemeFilter] = useState<"all" | "private" | "public">(
    "all"
  );

  const loadMemes = async () => {
    if (!supabase) return;
    try {
      const { data: memesData, error: memesError } = await supabase
        .from("meme_submissions")
        .select("*")
        .order("created_at", { ascending: false });

      if (memesError) throw memesError;
      setMemes(memesData || []);
    } catch (error) {
      console.error("Error loading memes:", error);
    }
  };

  const loadStickers = async () => {
    if (!supabase) return;
    try {
      const { data: stickersData, error: stickersError } = await supabase
        .from("stickers")
        .select("*")
        .order("created_at", { ascending: false });

      if (stickersError) throw stickersError;
      setStickers(stickersData || []);
    } catch (error) {
      console.error("Error loading stickers:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    if (!supabase) return;
    try {
      const { data: templatesData, error: templatesError } = await supabase
        .from("meme_templates")
        .select("*")
        .order("created_at", { ascending: false });

      if (templatesError) throw templatesError;
      setTemplates(templatesData || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const loadUsers = async () => {
    if (!supabase) return;
    try {
      // Use a custom SQL query to get users with meme counts
      const { data: usersData, error: usersError } = await supabase.rpc(
        "get_users_with_meme_counts"
      );

      if (usersError) {
        // Fallback to simple query if RPC doesn't exist
        const { data: simpleUsersData, error: simpleError } = await supabase
          .from("app_users")
          .select("*")
          .order("last_seen_at", { ascending: false });

        if (simpleError) throw simpleError;

        // Get meme counts separately
        const usersWithCounts = await Promise.all(
          simpleUsersData?.map(async (user) => {
            if (!supabase) return { ...user, meme_count: 0 };

            const { count } = await supabase
              .from("meme_submissions")
              .select("*", { count: "exact", head: true })
              .eq("creator_name", user.name);

            return {
              ...user,
              meme_count: count || 0,
            };
          }) || []
        );

        setUsers(usersWithCounts);
        return;
      }

      setUsers(usersData || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const loadData = useCallback(async () => {
    await Promise.all([
      loadMemes(),
      loadStickers(),
      loadTemplates(),
      loadUsers(),
    ]);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const deleteMeme = async (id: string) => {
    if (!confirm("האם אתה בטוח שברצונך למחוק את המימ הזה?")) return;
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from("meme_submissions")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setMemes(memes.filter((meme) => meme.id !== id));
    } catch (error) {
      console.error("Error deleting meme:", error);
      alert("שגיאה במחיקת המימ");
    }
  };

  const downloadMeme = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading meme:", error);
    }
  };

  const toggleMemePublic = async (id: string, isPublic: boolean) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from("meme_submissions")
        .update({ public: isPublic })
        .eq("id", id);

      if (error) throw error;

      // Update local state
      setMemes(
        memes.map((meme) =>
          meme.id === id ? { ...meme, public: isPublic } : meme
        )
      );
    } catch (error) {
      console.error("Error updating meme public status:", error);
      alert("שגיאה בעדכון סטטוס המימ");
    }
  };

  // Filter memes based on selected filter
  const filteredMemes = memes.filter((meme) => {
    switch (memeFilter) {
      case "public":
        return meme.public === true;
      case "private":
        return meme.public === false;
      case "all":
      default:
        return true;
    }
  });

  const deleteSticker = async (id: string) => {
    if (!confirm("האם אתה בטוח שברצונך למחוק את הסטיקר הזה?")) return;
    if (!supabase) return;

    try {
      const { error } = await supabase.from("stickers").delete().eq("id", id);

      if (error) throw error;

      setStickers(stickers.filter((sticker) => sticker.id !== id));
    } catch (error) {
      console.error("Error deleting sticker:", error);
      alert("שגיאה במחיקת הסטיקר");
    }
  };

  const uploadSticker = async (file: File, name: string, category: string) => {
    if (!supabase) return;
    setUploading(true);
    try {
      // Upload file with thumbnail to Supabase Storage
      const { imageUrl, thumbnailUrl } = await uploadImageWithThumbnail(
        supabase,
        file,
        "stickers",
        "",
        { maxWidth: 200, maxHeight: 200, quality: 0.8 }
      );

      // Save sticker metadata to database
      const { data, error: insertError } = await supabase
        .from("stickers")
        .insert({
          name,
          image_url: imageUrl,
          thumbnail_url: thumbnailUrl,
          category,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setStickers([data, ...stickers]);
      setShowUploadModal(false);
    } catch (error) {
      console.error("Error uploading sticker:", error);
      alert("שגיאה בהעלאת הסטיקר");
    } finally {
      setUploading(false);
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!confirm("האם אתה בטוח שברצונך למחוק את התבנית הזו?")) return;
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from("meme_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setTemplates(templates.filter((template) => template.id !== id));
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("שגיאה במחיקת התבנית");
    }
  };

  const downloadTemplate = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
    }
  };

  const uploadTemplate = async (file: File, name: string) => {
    if (!supabase) return;
    setUploading(true);
    try {
      // Upload file with thumbnail to Supabase Storage
      const { imageUrl, thumbnailUrl } = await uploadImageWithThumbnail(
        supabase,
        file,
        "templates",
        "",
        { maxWidth: 300, maxHeight: 300, quality: 0.8 }
      );

      // Save template metadata to database
      const { data, error: insertError } = await supabase
        .from("meme_templates")
        .insert({
          name,
          image_url: imageUrl,
          thumbnail_url: thumbnailUrl,
          category: "oz_and_tali", // Custom category for uploaded templates
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setTemplates([data, ...templates]);
      setShowTemplateModal(false);
    } catch (error) {
      console.error("Error uploading template:", error);
      alert("שגיאה בהעלאת התבנית");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-yellow-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg" dir="rtl">
            טוען מימז...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-yellow-100">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <AdminHeader
          activeTab={activeTab}
          memesCount={memes.length}
          stickersCount={stickers.length}
          templatesCount={templates.length}
          usersCount={users.length}
          onLogout={signOut}
        />

        {/* Tabs */}
        <AdminTabs
          activeTab={activeTab}
          memesCount={memes.length}
          stickersCount={stickers.length}
          templatesCount={templates.length}
          usersCount={users.length}
          onTabChange={setActiveTab}
          onAddSticker={() => setShowUploadModal(true)}
          onAddTemplate={() => setShowTemplateModal(true)}
        />

        {/* Content */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 shadow-xl border border-pink-100">
          {activeTab === "memes" ? (
            <>
              {/* Filter Controls */}
              <div className="mb-6 flex justify-center">
                <div className="rounded-lg p-2">
                  <div className="flex gap-2" dir="rtl">
                    <button
                      onClick={() => setMemeFilter("all")}
                      className={`w-24 py-3 rounded-lg text-sm font-bold transition-all duration-200 border ${
                        memeFilter === "all"
                          ? "bg-pink-500 text-white border-pink-500 shadow-md"
                          : "text-gray-700 hover:bg-pink-100 border-pink-200 bg-white"
                      }`}
                    >
                      הכל ({memes.length})
                    </button>
                    <button
                      onClick={() => setMemeFilter("private")}
                      className={`w-24 py-3 rounded-lg text-sm font-bold transition-all duration-200 border ${
                        memeFilter === "private"
                          ? "bg-pink-500 text-white border-pink-500 shadow-md"
                          : "text-gray-700 hover:bg-pink-100 border-pink-200 bg-white"
                      }`}
                    >
                      פרטי ({memes.filter((m) => m.public === false).length})
                    </button>
                    <button
                      onClick={() => setMemeFilter("public")}
                      className={`w-24 py-3 rounded-lg text-sm font-bold transition-all duration-200 border ${
                        memeFilter === "public"
                          ? "bg-pink-500 text-white border-pink-500 shadow-md"
                          : "text-gray-700 hover:bg-pink-100 border-pink-200 bg-white"
                      }`}
                    >
                      ציבורי ({memes.filter((m) => m.public === true).length})
                    </button>
                  </div>
                </div>
              </div>

              <MemesGrid
                memes={filteredMemes}
                onDeleteMeme={deleteMeme}
                onDownloadMeme={downloadMeme}
                onTogglePublic={toggleMemePublic}
              />
            </>
          ) : activeTab === "stickers" ? (
            <StickersGrid stickers={stickers} onDeleteSticker={deleteSticker} />
          ) : activeTab === "templates" ? (
            <TemplatesGrid
              templates={templates}
              onDeleteTemplate={deleteTemplate}
              onDownloadTemplate={downloadTemplate}
            />
          ) : (
            <UsersGrid users={users} />
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        uploading={uploading}
        onSubmit={uploadSticker}
        onCancel={() => setShowUploadModal(false)}
      />

      {/* Template Upload Modal */}
      <UploadTemplateModal
        isOpen={showTemplateModal}
        uploading={uploading}
        onSubmit={uploadTemplate}
        onCancel={() => setShowTemplateModal(false)}
      />
    </div>
  );
};

export default AdminDashboard;
